import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { nextTick } from 'vue';
import router from '@/router';
import { getTour } from '@/help/index.js';
import { useAuthStore } from '@/stores/auth-store.js';
import { useHelpStore } from '@/stores/help-store.js';

let activeDriver = null;
let activeTourId = null;

const waitForElement = (selector, timeoutMs = 5000) => {
    return new Promise((resolve) => {
        if (typeof document === 'undefined' || !selector) {
            resolve(null);
            return;
        }

        const existing = document.querySelector(selector);
        if (existing) {
            resolve(existing);
            return;
        }

        const started = Date.now();
        const timer = setInterval(() => {
            const el = document.querySelector(selector);
            if (el || Date.now() - started >= timeoutMs) {
                clearInterval(timer);
                resolve(el || null);
            }
        }, 80);
    });
};

const clickElement = (selectorOrEl) => {
    const el =
        typeof selectorOrEl === 'string' ? document.querySelector(selectorOrEl) : selectorOrEl;
    if (!el || typeof el.click !== 'function') {
        return false;
    }
    el.click();
    return true;
};

/**
 * Interactive feature walkthroughs via driver.js, synced to help progress API.
 *
 * Tour step JSON supports:
 * - interact: 'click' | 'require-click' — click the target (auto on Next, or user must click)
 * - clickSelector — optional alternate click target
 * - waitFor — selector that must appear after the click before advancing
 * - closeSelector — clicked when leaving the step (e.g. close a modal)
 * - disableInteraction — block clicks on the highlighted element
 * - skipIfMissing — omit step when element is absent at highlight time
 */
export function useHelpTour() {
    const helpStore = useHelpStore();
    const auth = useAuthStore();

    const destroyActive = () => {
        if (activeDriver) {
            try {
                activeDriver.destroy();
            } catch {
                /* already destroyed */
            }
        }
        activeDriver = null;
        activeTourId = null;
    };

    const canRunTour = (tour) => {
        if (!tour) {
            return false;
        }
        const permissions = tour.permissions ?? [];
        if (!permissions.length) {
            return true;
        }
        return auth.hasAccess({ permissions });
    };

    const syncStep = async (tourId, index, total, { completed = false, closed = false } = {}) => {
        if (completed) {
            await helpStore.updateTourProgress(tourId, {
                status: 'completed',
                step_index: Math.max(total - 1, 0),
                completed_at: new Date().toISOString(),
            });
            return;
        }

        if (closed) {
            const previous = helpStore.getTourProgress(tourId);
            if (previous.status === 'completed') {
                return;
            }
            await helpStore.updateTourProgress(tourId, {
                status: 'in_progress',
                step_index: index,
            });
            return;
        }

        await helpStore.updateTourProgress(tourId, {
            status: 'in_progress',
            step_index: index,
        });
    };

    const runStepInteraction = async (stepDef, element) => {
        if (!stepDef?.interact) {
            return true;
        }

        const clickTarget = stepDef.clickSelector || element || stepDef.element;
        clickElement(clickTarget);
        await nextTick();

        if (stepDef.waitFor) {
            const appeared = await waitForElement(stepDef.waitFor, stepDef.waitTimeoutMs || 6000);
            if (!appeared) {
                return false;
            }
            await nextTick();
            // Give Headless UI transitions a beat to finish.
            await new Promise((r) => setTimeout(r, 200));
        }

        return true;
    };

    const buildDriverSteps = (tour) => {
        return (tour.steps || []).map((stepDef) => {
            const isInteractive = stepDef.interact === 'click' || stepDef.interact === 'require-click';
            const requireClick = stepDef.interact === 'require-click';

            const driveStep = {
                element: stepDef.element || undefined,
                disableActiveInteraction: stepDef.disableInteraction === true ? true : false,
                advanceOnClick: requireClick,
                skipMissingElement: stepDef.skipIfMissing !== false,
                // Allow time for prior interactive steps to open modals/forms.
                waitForElement: stepDef.waitForElementMs ?? (stepDef.element ? 5000 : 0),
                data: { stepDef },
                popover: {
                    title: stepDef.title,
                    description: stepDef.description,
                    side: stepDef.side || 'bottom',
                    align: stepDef.align || 'start',
                    nextBtnText: requireClick
                        ? stepDef.nextBtnText || 'Click highlighted control'
                        : isInteractive
                          ? stepDef.nextBtnText || 'Open & continue'
                          : stepDef.nextBtnText || 'Next',
                    onNextClick: async (_el, _step, { driver: drv }) => {
                        const def = stepDef;
                        if (def.interact === 'click' || def.interact === 'require-click') {
                            const ok = await runStepInteraction(def, _el);
                            if (!ok && def.waitFor) {
                                // Still advance so the tour does not soft-lock.
                                drv.moveNext();
                                return;
                            }
                            // Refresh stage after DOM changes (modal/form open).
                            drv.refresh();
                            await nextTick();
                        }
                        drv.moveNext();
                    },
                },
                onDeselected: async () => {
                    if (stepDef.closeSelector) {
                        clickElement(stepDef.closeSelector);
                        await nextTick();
                    }
                },
            };

            return driveStep;
        });
    };

    /**
     * @param {string} tourId
     * @param {{ resume?: boolean, forceRestart?: boolean }} [options]
     */
    const startTour = async (tourId, options = {}) => {
        const { resume = false, forceRestart = false } = options;
        const tour = getTour(tourId);

        if (!canRunTour(tour) || !tour.steps?.length) {
            return false;
        }

        destroyActive();

        if (tour.route && router.currentRoute.value.name !== tour.route) {
            await router.push({ name: tour.route });
            await nextTick();
            const firstSelector = tour.steps.find((s) => s.element)?.element;
            if (firstSelector) {
                await waitForElement(firstSelector, 5000);
            }
        } else {
            await nextTick();
        }

        const progress = helpStore.getTourProgress(tourId);
        let startIndex = 0;
        if (resume && !forceRestart && progress.status === 'in_progress') {
            startIndex = Math.min(progress.step_index || 0, tour.steps.length - 1);
        }

        if (forceRestart || progress.status === 'not_started' || progress.status === 'completed') {
            await helpStore.updateTourProgress(tourId, {
                status: 'in_progress',
                step_index: startIndex,
                completed_at: forceRestart ? null : progress.completed_at,
            });
        }

        const steps = buildDriverSteps(tour);
        if (!steps.length) {
            return false;
        }

        startIndex = Math.min(startIndex, steps.length - 1);
        activeTourId = tourId;

        activeDriver = driver({
            showProgress: true,
            animate: true,
            allowClose: true,
            smoothScroll: true,
            overlayOpacity: 0.55,
            stagePadding: 8,
            stageRadius: 8,
            // Default: allow clicking highlighted controls (forms, buttons).
            disableActiveInteraction: false,
            steps,
            onHighlighted: (_el, _step, { driver: drv }) => {
                const index = drv.getActiveIndex() ?? 0;
                syncStep(tourId, index, steps.length);
            },
            onDestroyStarted: (_el, _step, { driver: drv }) => {
                const index = drv.getActiveIndex() ?? 0;
                if (!drv.hasNextStep()) {
                    syncStep(tourId, index, steps.length, { completed: true });
                } else {
                    syncStep(tourId, index, steps.length, { closed: true });
                }
                drv.destroy();
                activeDriver = null;
                activeTourId = null;
            },
        });

        activeDriver.drive(startIndex);
        return true;
    };

    const resumeTour = (tourId) => startTour(tourId, { resume: true });

    const restartTour = (tourId) => startTour(tourId, { forceRestart: true, resume: false });

    const stopTour = () => {
        destroyActive();
    };

    const isTourRunning = () => !!activeDriver && !!activeTourId;

    return {
        startTour,
        resumeTour,
        restartTour,
        stopTour,
        isTourRunning,
        canRunTour,
        getTour,
    };
}
